import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFragment } from 'react-relay/hooks';
import { appSubscriptionCheck$key } from 'src/components/__generated__/appSubscriptionCheck.graphql';
import { appSubscriptionCheckFragment } from 'src/components/appSubscriptionCheck';
import { FeatureFlagNames } from 'src/generated/feature-flags';
import { useAppState } from 'src/shared/appState';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import { OPEN_AGENT_GUIDE_EVENT } from '../inlineHelpLayout/inlineHelpLayout';

export interface AgentGuideRequest {
    articleId: string;
    requestKey: number;
}

interface AgentAssistantContextValue {
    isEnabled: boolean;
    isOpen: boolean;
    isFullScreen: boolean;
    animateFullScreenEntry: boolean;
    /** Message a caller asked the agent to start from; consumed by the launcher when it opens a new conversation. */
    initialPrompt?: string;
    openSidebar: () => void;
    openAiMode: () => void;
    /** Opens AI mode on a new conversation that starts from this message. */
    startWithPrompt: (prompt: string) => void;
    openClassicMode: () => void;
    close: () => void;
    changeFullScreen: (isFullScreen: boolean) => void;
    guideRequest: AgentGuideRequest | null;
}

const AgentAssistantContext = createContext<AgentAssistantContextValue | null>(null);

export const AgentAssistantProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { userInfo } = useAccountData();
    const { queryData } = useAppState();
    const subscriptionCheck = useFragment<appSubscriptionCheck$key>(appSubscriptionCheckFragment, queryData?.appSubscriptionCheck ?? null);
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [animateFullScreenEntry, setAnimateFullScreenEntry] = useState(false);
    const [guideRequest, setGuideRequest] = useState<AgentGuideRequest | null>(null);
    const [initialPrompt, setInitialPrompt] = useState<string>();
    const featureFlagContext = { userId: userInfo?.userID?.toString() };
    // appSubscriptionCheck queries only active subscriptions; a trial is included in this result.
    const hasActiveSubscription = Boolean(subscriptionCheck?.subscription && !subscriptionCheck.subscription.isCancelled);
    const isEnabledByFeatureFlag = useFeatureFlagWithConditions(FeatureFlagNames.BOAIAGENT, featureFlagContext);
    const isEnabled = isEnabledByFeatureFlag && hasActiveSubscription;
    const shouldStartWithAiAgent = useFeatureFlagWithConditions(FeatureFlagNames.START_WITH_AIAGENT, featureFlagContext);

    const openSidebar = useCallback(() => {
        setGuideRequest(null);
        setInitialPrompt(undefined);
        setAnimateFullScreenEntry(false);
        setIsFullScreen(false);
        setIsOpen(true);
    }, []);

    useEffect(() => {
        const openGuide = (event: Event) => {
            const articleId = (event as CustomEvent<{ articleId?: string }>).detail?.articleId;
            if (!articleId || !/^\d+$/.test(articleId)) return;
            setInitialPrompt(undefined);
            setGuideRequest({ articleId, requestKey: Date.now() });
            setAnimateFullScreenEntry(false);
            setIsFullScreen(false);
            setIsOpen(true);
        };
        window.addEventListener(OPEN_AGENT_GUIDE_EVENT, openGuide);
        return () => window.removeEventListener(OPEN_AGENT_GUIDE_EVENT, openGuide);
    }, []);

    const openAiMode = useCallback(() => {
        setGuideRequest(null);
        setInitialPrompt(undefined);
        setAnimateFullScreenEntry(true);
        setIsFullScreen(true);
        setIsOpen(true);
    }, []);

    const startWithPrompt = useCallback((prompt: string) => {
        setGuideRequest(null);
        setInitialPrompt(prompt);
        setAnimateFullScreenEntry(true);
        setIsFullScreen(true);
        setIsOpen(true);
    }, []);

    const openClassicMode = useCallback(() => {
        setAnimateFullScreenEntry(false);
        setIsFullScreen(false);
        setIsOpen(false);
    }, []);

    const close = useCallback(() => {
        // Dropped here so reopening the agent later does not replay a prompt the user already sent.
        setInitialPrompt(undefined);

        if (isFullScreen) {
            openClassicMode();
            return;
        }

        setIsOpen(false);
    }, [isFullScreen, openClassicMode]);

    const changeFullScreen = useCallback((fullScreen: boolean) => {
        setAnimateFullScreenEntry(fullScreen);
        setIsFullScreen(fullScreen);
    }, []);

    useEffect(() => {
        if (!isEnabled) {
            setAnimateFullScreenEntry(false);
            setIsFullScreen(false);
            setIsOpen(false);
            return;
        }

        if (shouldStartWithAiAgent) {
            setAnimateFullScreenEntry(false);
            setIsFullScreen(true);
            setIsOpen(true);
        }
    }, [isEnabled, shouldStartWithAiAgent]);

    const value = useMemo<AgentAssistantContextValue>(
        () => ({
            isEnabled,
            isOpen,
            isFullScreen,
            animateFullScreenEntry,
            initialPrompt,
            openSidebar,
            openAiMode,
            startWithPrompt,
            openClassicMode,
            close,
            changeFullScreen,
            guideRequest
        }),
        [
            animateFullScreenEntry,
            changeFullScreen,
            close,
            guideRequest,
            initialPrompt,
            isEnabled,
            isFullScreen,
            isOpen,
            openAiMode,
            openClassicMode,
            openSidebar,
            startWithPrompt
        ]
    );

    return <AgentAssistantContext.Provider value={value}>{children}</AgentAssistantContext.Provider>;
};

export const useAgentAssistant = () => {
    const context = useContext(AgentAssistantContext);
    if (!context) throw new Error('useAgentAssistant must be used within an AgentAssistantProvider');
    return context;
};
